import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, User, Clock, RefreshCw, ArrowLeft, UserPlus, X, Search, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt12 = (t) => {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh);
  const p = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${p}`;
};

const getMembershipLabel = (booking) => {
  // New bookings: access_label was stored at booking time — always accurate
  if (booking.access_label) return { text: booking.access_label, color: getLabelColor(booking.access_label) };

  // Old bookings: reconstruct from member's current/historical state
  const plan = booking.membership_plan;
  const credits = booking.class_credits || 0;
  const adminGrant = booking.admin_granted_unlimited;
  const firstClassFreeUsed = booking.first_class_free_used;

  if (adminGrant) return { text: "Admin Grant — Unlimited", color: "text-[#FFB612]" };
  if (plan === "elite" || plan === "elite_plus") return { text: "Elite Unlimited", color: "text-purple-400" };
  if (plan === "elite_plus_family") return { text: "Elite Unlimited Plus", color: "text-purple-400" };
  if (plan === "family_plan" || plan === "family_member") return { text: "Family Plan", color: "text-blue-400" };
  if (plan === "performance") return { text: "Performance — 6/mo", color: "text-blue-400" };
  if (plan === "starter") return { text: "Starter — 3/mo", color: "text-green-400" };
  if (plan === "free_week") return { text: "Free Week Pass", color: "text-[#FFB612]" };
  // Class credits — distinguish first class free from purchased single class
  if (credits > 0) return { text: "Single Class Credit", color: "text-green-400" };
  if (firstClassFreeUsed) return { text: "First Class Free", color: "text-[#FFB612]" };
  // credits == 0 and no plan — they used a credit that has since been deducted
  return { text: "Single Class", color: "text-green-400" };
};

const getLabelColor = (label) => {
  if (!label) return "text-white/40";
  const l = label.toLowerCase();
  if (l.includes("elite") || l.includes("unlimited")) return "text-purple-400";
  if (l.includes("family")) return "text-blue-400";
  if (l.includes("performance")) return "text-blue-400";
  if (l.includes("starter")) return "text-green-400";
  if (l.includes("free week")) return "text-[#FFB612]";
  if (l.includes("first class free")) return "text-[#FFB612]";
  if (l.includes("admin")) return "text-[#FFB612]";
  if (l.includes("credit") || l.includes("single")) return "text-green-400";
  return "text-white/50";
};

const getWeekDates = () => {
  const dates = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push(d.toISOString().split("T")[0]);
  }
  return dates;
};

export const CheckInPage = () => {
  const navigate = useNavigate();
  const weekDates = getWeekDates();
  const [selectedDate, setSelectedDate] = useState(weekDates[0]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(true);

  // Walk-in modal state
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [walkInSearch, setWalkInSearch] = useState("");
  const [walkInResults, setWalkInResults] = useState([]);
  const [walkInSearching, setWalkInSearching] = useState(false);
  const [walkInAdding, setWalkInAdding] = useState(null);

  const selectedClassRef = useRef(null);

  // Keep ref in sync with state — ref is always current, safe to use in intervals
  useEffect(() => {
    selectedClassRef.current = selectedClass;
  }, [selectedClass]);

  useEffect(() => {
    loadClasses(selectedDate);
    const interval = setInterval(() => loadClasses(selectedDate), 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const loadClasses = async (date) => {
    try {
      const response = await axios.get(`${API}/checkin/today?date=${date}`);
      setClasses(response.data);

      // Use ref (always current) — not stale closure
      const current = selectedClassRef.current;
      if (current) {
        // Keep the same class selected, just refresh its data
        const updated = response.data.find(c => c.class.id === current.class.id);
        if (updated) setSelectedClass(updated);
        // If class no longer on this date, keep showing it (don't reset)
      } else if (response.data.length > 0) {
        // Nothing selected yet — auto-select first class
        setSelectedClass(response.data[0]);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    setSelectedClass(null);
    setLoading(true);
  };

  const handleCheckIn = async (bookingId, memberName) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/check-in`);
      toast.success(`${memberName} checked in!`);
      loadClasses(selectedDate);
    } catch {
      toast.error("Check-in failed");
    }
  };

  // Walk-in search
  const handleWalkInSearch = async (q) => {
    setWalkInSearch(q);
    if (q.length < 2) { setWalkInResults([]); return; }
    setWalkInSearching(true);
    try {
      const adminToken = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/members/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setWalkInResults(res.data.slice(0, 8));
    } catch {} finally { setWalkInSearching(false); }
  };

  const handleWalkIn = async (memberId, memberName) => {
    if (!selectedClass) return toast.error("Please select a class first");
    setWalkInAdding(memberId);
    try {
      const res = await axios.post(`${API}/checkin/walk-in`, {
        class_id: selectedClass.class.id,
        member_id: memberId
      });
      toast.success(res.data.message);
      setShowWalkIn(false);
      setWalkInSearch("");
      setWalkInResults([]);
      loadClasses(selectedDate);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add member");
    } finally { setWalkInAdding(null); }
  };

  if (loading && classes.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/admin/dashboard")} className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />Admin
          </button>
          <div>
            <img src="/nbc_logo.jpg" alt="NBC" className="h-12 md:h-16" />
            <p className="text-[#FFB612] text-sm uppercase tracking-wider mt-1">Member Check-In</p>
          </div>
        </div>
        <Button onClick={() => loadClasses(selectedDate)} variant="outline" className="border-white/20 text-white">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {/* 7-Day Date Selector */}
      <div className="grid grid-cols-7 gap-px w-full mb-6">
        {weekDates.map((date) => {
          const d = new Date(date + "T00:00:00");
          const isToday = date === weekDates[0];
          return (
            <button key={date} onClick={() => handleSelectDate(date)}
              className={`py-2 border transition-all text-center ${selectedDate === date ? "bg-[#FFB612] text-black border-[#FFB612] font-bold" : "bg-[#0A0A0A] border-white/10 hover:border-white/30 text-white"}`}>
              <p className="text-[10px] uppercase">{isToday ? "Today" : d.toLocaleDateString("en-US", { weekday: "short" })}</p>
              <p className="text-sm font-bold">{d.getDate()}</p>
            </button>
          );
        })}
      </div>

      {classes.length === 0 ? (
        <div className="text-center py-20"><p className="text-2xl text-white/50">No classes scheduled for this day</p></div>
      ) : (
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Class List */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="text-lg font-bold uppercase mb-4">Classes</h2>
            {classes.map((item) => (
              <button key={item.class.id} onClick={() => setSelectedClass(item)}
                className={`w-full text-left p-4 border transition-all ${selectedClass?.class.id === item.class.id ? "bg-[#FFB612]/10 border-[#FFB612]" : "bg-[#0A0A0A] border-white/10 hover:border-white/30"}`}>
                <p className="font-bold">{item.class.class_type}</p>
                <div className="flex items-center gap-2 text-sm text-white/60 mt-1">
                  <Clock className="w-4 h-4" /><span>{fmt12(item.class.time)}</span>
                </div>
                <p className="text-sm text-white/40 mt-1">{item.bookings.filter(b => b.checked_in).length}/{item.bookings.length} checked in</p>
              </button>
            ))}
          </div>

          {/* Check-In Panel */}
          <div className="lg:col-span-3">
            {selectedClass && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold uppercase">{selectedClass.class.class_type}</h2>
                    <p className="text-white/60">{fmt12(selectedClass.class.time)} · {selectedClass.class.duration} min · {selectedClass.class.trainer_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-mono font-bold text-[#FFB612]">{selectedClass.bookings.filter(b => b.checked_in).length}</p>
                    <p className="text-sm text-white/50">of {selectedClass.bookings.length} checked in</p>
                  </div>
                </div>

                {selectedClass.bookings.length === 0 ? (
                  <div className="bg-[#0A0A0A] border border-white/10 p-12 text-center">
                    <p className="text-white/50">No bookings for this class</p>
                    <p className="text-white/30 text-sm mt-2">Use the + button to add walk-in members</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedClass.bookings.map((booking) => (
                      <div key={booking.id} className={`p-6 border transition-all ${booking.checked_in ? "bg-green-500/10 border-green-500/30" : "bg-[#0A0A0A] border-white/10"}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${booking.checked_in ? "bg-green-500/20" : "bg-white/10"}`}>
                              {booking.checked_in ? <Check className="w-6 h-6 text-green-400" /> : <User className="w-6 h-6 text-white/50" />}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-lg">{booking.member_name}</p>
                                {booking.is_first_booking && (
                                  <span className="flex items-center gap-1 text-[#FFB612] text-xs font-bold bg-[#FFB612]/10 border border-[#FFB612]/30 px-2 py-0.5 rounded-sm">
                                    <Star className="w-3 h-3 fill-[#FFB612]" />
                                    First Timer!
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-white/50">
                                {booking.checked_in ? "Checked In" : "Not Checked In"}
                                {booking.walk_in && <span className="ml-2 text-xs text-[#FFB612]">Walk-in</span>}
                              </p>
                              {(() => {
                                const label = getMembershipLabel(booking);
                                return <p className={`text-xs font-medium mt-0.5 ${label.color}`}>{label.text}</p>;
                              })()}
                            </div>
                          </div>
                          {!booking.checked_in && (
                            <Button onClick={() => handleCheckIn(booking.id, booking.member_name)}
                              className="bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold px-8"
                              data-testid={`checkin-btn-${booking.id}`}>
                              CHECK IN
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Floating Walk-In Button */}
      <button
        onClick={() => setShowWalkIn(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#FFB612] text-black rounded-full flex items-center justify-center shadow-lg hover:bg-[#E5A00D] transition-colors z-40"
        data-testid="walk-in-btn"
        title="Add walk-in member"
      >
        <UserPlus className="w-6 h-6" />
      </button>

      {/* Walk-In Modal */}
      {showWalkIn && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-[#FFB612]/30 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="h-1 w-full bg-[#FFB612]" />
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold uppercase">Add Walk-In Member</h3>
                  {selectedClass && <p className="text-xs text-white/50 mt-0.5">{selectedClass.class.class_type} · {fmt12(selectedClass.class.time)}</p>}
                </div>
                <button onClick={() => { setShowWalkIn(false); setWalkInSearch(""); setWalkInResults([]); }} className="text-white/30 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!selectedClass && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 mb-4 text-sm text-yellow-400">
                  Please select a class first from the list.
                </div>
              )}

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  placeholder="Search member by name or phone..."
                  value={walkInSearch}
                  onChange={e => handleWalkInSearch(e.target.value)}
                  className="w-full bg-[#050505] border border-white/20 pl-9 pr-4 py-2.5 text-white placeholder-white/30 focus:border-[#FFB612] focus:outline-none"
                  autoFocus
                  data-testid="walk-in-search"
                />
              </div>

              {walkInSearching && <p className="text-white/40 text-sm text-center py-4">Searching...</p>}

              {walkInResults.length > 0 && (
                <div className="space-y-2">
                  {walkInResults.map(m => {
                    const name = `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.name || 'Member';
                    return (
                      <button
                        key={m.id}
                        onClick={() => handleWalkIn(m.id, name)}
                        disabled={walkInAdding === m.id}
                        className="w-full flex items-center justify-between p-3 border border-white/10 hover:border-[#FFB612]/50 hover:bg-[#FFB612]/5 transition-all text-left"
                        data-testid={`walk-in-member-${m.id}`}
                      >
                        <div>
                          <p className="font-bold">{name}</p>
                          <p className="text-xs text-white/40">{m.phone} · {m.membership_plan || 'No plan'}</p>
                        </div>
                        <span className={`text-xs px-3 py-1 font-bold ${walkInAdding === m.id ? 'bg-white/10 text-white/50' : 'bg-[#FFB612] text-black'}`}>
                          {walkInAdding === m.id ? "Adding..." : "Add to Class"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {walkInSearch.length >= 2 && !walkInSearching && walkInResults.length === 0 && (
                <p className="text-white/40 text-sm text-center py-4">No members found for "{walkInSearch}"</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
